import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

interface Toast {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    duration?: number;
}

interface ToastContextType {
    showToast: (toast: Omit<Toast, 'id'>) => void;
    showError: (title: string, message?: string) => void;
    showSuccess: (title: string, message?: string) => void;
    showWarning: (title: string, message?: string) => void;
    showInfo: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

const ToastItem: React.FC<{ toast: Toast; onClose: (id: string) => void }> = ({ toast, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    const duration = toast.duration || 3000;

    useEffect(() => {
        if (isPaused) return;

        const timer = setTimeout(() => {
            setIsExiting(true);
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, isPaused]);

    const handleAnimationEnd = () => {
        if (isExiting) {
            onClose(toast.id);
        }
    };

    const handleClose = () => {
        setIsExiting(true);
    };

    const icons = {
        success: <CheckCircle className="w-6 h-6 text-emerald-500" />,
        error: <AlertCircle className="w-6 h-6 text-rose-500" />,
        warning: <AlertTriangle className="w-6 h-6 text-amber-500" />,
        info: <Info className="w-6 h-6 text-blue-500" />,
    };

    const styles = {
        success: 'bg-white dark:bg-gray-900 border-emerald-100 dark:border-emerald-900/30 shadow-[0_8px_30px_rgb(16,185,129,0.15)]',
        error: 'bg-white dark:bg-gray-900 border-rose-100 dark:border-rose-900/30 shadow-[0_8px_30px_rgb(244,63,94,0.15)]',
        warning: 'bg-white dark:bg-gray-900 border-amber-100 dark:border-amber-900/30 shadow-[0_8px_30px_rgb(245,158,11,0.15)]',
        info: 'bg-white dark:bg-gray-900 border-blue-100 dark:border-blue-900/30 shadow-[0_8px_30px_rgb(59,130,246,0.15)]',
    };

    return (
        <div
            className={`
                flex items-start gap-4 p-4 rounded-2xl border 
                backdrop-blur-md transition-all duration-300
                ${styles[toast.type]}
                ${isExiting ? 'animate-slide-out-top' : 'animate-slide-in-top'}
            `}
            style={{
                minWidth: '340px',
                maxWidth: '450px',
                pointerEvents: 'auto'
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className="flex-shrink-0 pt-0.5">{icons[toast.type]}</div>
            <div className="flex-1 min-w-0">
                {/* <h4 className="font-semibold text-base text-gray-900 dark:text-gray-100 leading-tight mb-1">
                    {toast.title}
                </h4> */}
                {toast.message && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {toast.message}
                    </p>
                )}
            </div>
            <button
                onClick={handleClose}
                className="flex-shrink-0 -mr-1 -mt-1 p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const DEFAULT_DURATION = 4500;
    const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        const newToast = { ...toast, id };
        setToasts((prev) => [...prev, newToast]);
    }, []);

    const showError = useCallback((title: string, message?: string) => {
        showToast({ type: 'error', title, message, duration: DEFAULT_DURATION });
    }, [showToast]);

    const showSuccess = useCallback((title: string, message?: string) => {
        showToast({ type: 'success', title, message, duration: DEFAULT_DURATION });
    }, [showToast]);

    const showWarning = useCallback((title: string, message?: string) => {
        showToast({ type: 'warning', title, message, duration: DEFAULT_DURATION });
    }, [showToast]);

    const showInfo = useCallback((title: string, message?: string) => {
        showToast({ type: 'info', title, message, duration: DEFAULT_DURATION });
    }, [showToast]);

    const closeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, showError, showSuccess, showWarning, showInfo }}>
            {children}
            <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-3 pointer-events-none items-center">
                {toasts.map((toast) => (
                    <ToastItem key={toast.id} toast={toast} onClose={closeToast} />
                ))}
            </div>
            <style>{`
                @keyframes slide-in-top {
                    0% { 
                        transform: translateY(-100%) scale(0.9); 
                        opacity: 0; 
                    }
                    100% { 
                        transform: translateY(0) scale(1); 
                        opacity: 1; 
                    }
                }
                @keyframes slide-out-top {
                    0% { 
                        transform: translateY(0) scale(1); 
                        opacity: 1; 
                    }
                    100% { 
                        transform: translateY(-150%) scale(0.9); 
                        opacity: 0; 
                    }
                }
                .animate-slide-in-top { 
                    animation: slide-in-top 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
                }
                .animate-slide-out-top { 
                    animation: slide-out-top 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
                }
            `}</style>
        </ToastContext.Provider>
    );
};